// Manages replay playback: feeds stored events into the existing game rendering systems

import * as THREE from 'three';
import { Arena } from '../scene/Arena';
import { WizardModel } from '../models/WizardModel';
import { Fireball } from '../spells/Fireball';
import { IceShard } from '../spells/IceShard';
import { Shield } from '../spells/Shield';
import { LightningBolt } from '../spells/LightningBolt';
import { HealSpell } from '../spells/HealSpell';
import { Spell } from '../spells/Spell';
import { PostProcessing } from '../effects/PostProcessing';
import { EnvironmentalEffects } from '../effects/EnvironmentalEffects';
import {
  ReplayData,
  ReplayEvent,
  ReplaySpellEvent,
  ReplayDamageEvent,
  ReplayState,
  ReplaySpeed,
  CameraMode,
} from './ReplayTypes';
import { detectHighlights } from './HighlightDetector';
import { ReplayUI } from './ReplayUI';
import { AudioManager } from '../audio/AudioManager';

const PLAYER1_POS = new THREE.Vector3(0, 1, 8);
const PLAYER2_POS = new THREE.Vector3(0, 1, -8);
const MAX_HEALTH = 100;

export class ReplayManager {
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private renderer: THREE.WebGLRenderer;
  private clock: THREE.Clock;

  private arena: Arena | null = null;
  private player1Model: WizardModel | null = null;
  private player2Model: WizardModel | null = null;
  private player1Group: THREE.Group | null = null;
  private player2Group: THREE.Group | null = null;
  private player1Label: THREE.Sprite | null = null;
  private player2Label: THREE.Sprite | null = null;

  private activeSpells: Set<Spell> = new Set();
  private player1Shield: Shield | null = null;
  private player2Shield: Shield | null = null;

  private postProcessing: PostProcessing | null = null;
  private environmentalEffects: EnvironmentalEffects | null = null;
  private audioManager: AudioManager;

  private replayData: ReplayData | null = null;
  private state: ReplayState = {
    isPlaying: false,
    currentTime: 0,
    speed: 1,
    cameraMode: 'free',
    highlights: [],
    currentHighlightIndex: -1,
    watchingHighlights: false,
  };

  private nextEventIndex: number = 0;
  private animationFrameId: number = 0;
  private replayUI: ReplayUI | null = null;

  // Camera orbit state for free mode
  private cameraAngle: number = 0;
  private cameraRadius: number = 15;
  private cameraHeight: number = 8;
  private cameraRotationSpeed: number = 0.15;

  // Mouse drag orbit
  private isDragging: boolean = false;
  private lastMouseX: number = 0;
  private lastMouseY: number = 0;

  // Health tracking for UI
  private player1Health: number = MAX_HEALTH;
  private player2Health: number = MAX_HEALTH;

  // Callbacks
  private onExitCallback: (() => void) | null = null;

  constructor() {
    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      100
    );
    const canvas = document.getElementById('game-canvas') as HTMLCanvasElement;
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
    this.clock = new THREE.Clock();
    this.audioManager = new AudioManager();

    this.setupRenderer();
    this.setupCamera();
    this.setupMouseControls();
    this.handleResize = this.handleResize.bind(this);
    window.addEventListener('resize', this.handleResize);
  }

  private setupRenderer(): void {
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.2;
  }

  private setupCamera(): void {
    this.camera.position.set(0, 8, 15);
    this.camera.lookAt(0, 0, 0);
  }

  private setupMouseControls(): void {
    const canvas = this.renderer.domElement;

    canvas.addEventListener('mousedown', (e: MouseEvent) => {
      if (this.state.cameraMode !== 'free') return;
      this.isDragging = true;
      this.lastMouseX = e.clientX;
      this.lastMouseY = e.clientY;
    });

    canvas.addEventListener('mousemove', (e: MouseEvent) => {
      if (!this.isDragging || this.state.cameraMode !== 'free') return;
      const dx = e.clientX - this.lastMouseX;
      const dy = e.clientY - this.lastMouseY;
      this.cameraAngle += dx * 0.005;
      this.cameraHeight = Math.max(3, Math.min(15, this.cameraHeight - dy * 0.05));
      this.lastMouseX = e.clientX;
      this.lastMouseY = e.clientY;
    });

    canvas.addEventListener('mouseup', () => {
      this.isDragging = false;
    });

    canvas.addEventListener('wheel', (e: WheelEvent) => {
      if (this.state.cameraMode !== 'free') return;
      this.cameraRadius = Math.max(5, Math.min(25, this.cameraRadius + e.deltaY * 0.01));
    });
  }

  private handleResize(): void {
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(window.innerWidth, window.innerHeight);
  }

  onExit(callback: () => void): void {
    this.onExitCallback = callback;
  }

  async loadReplay(data: ReplayData): Promise<void> {
    this.replayData = data;
    this.state.highlights = detectHighlights(data);
    this.state.currentTime = 0;
    this.state.isPlaying = false;
    this.state.speed = 1;
    this.state.cameraMode = 'free';
    this.state.currentHighlightIndex = -1;
    this.state.watchingHighlights = false;
    this.nextEventIndex = 0;
    this.player1Health = MAX_HEALTH;
    this.player2Health = MAX_HEALTH;

    // Build the scene
    this.arena = new Arena(this.scene);
    this.postProcessing = new PostProcessing(this.renderer, this.scene, this.camera);
    this.environmentalEffects = new EnvironmentalEffects(this.scene);

    await this.audioManager.init().catch(() => {});

    // Create wizard models
    this.player1Model = new WizardModel(data.player1Wizard);
    this.player1Group = this.player1Model.getGroup();
    this.player1Group.position.copy(PLAYER1_POS);
    this.player1Group.position.y = 0;
    this.scene.add(this.player1Group);

    this.player2Model = new WizardModel(data.player2Wizard);
    this.player2Group = this.player2Model.getGroup();
    this.player2Group.position.copy(PLAYER2_POS);
    this.player2Group.position.y = 0;
    this.player2Group.rotation.y = Math.PI;
    this.scene.add(this.player2Group);

    // Name labels
    this.player1Label = this.createTextSprite(data.player1Name, '#44FF44');
    this.player1Label.position.set(PLAYER1_POS.x, 4.5, PLAYER1_POS.z);
    this.scene.add(this.player1Label);

    this.player2Label = this.createTextSprite(data.player2Name, '#FF4444');
    this.player2Label.position.set(PLAYER2_POS.x, 4.5, PLAYER2_POS.z);
    this.scene.add(this.player2Label);

    // Create replay UI
    this.replayUI = new ReplayUI(data, this.state);
    this.replayUI.onPlayPause(() => this.togglePlayPause());
    this.replayUI.onSpeedChange((speed) => this.setSpeed(speed));
    this.replayUI.onSeek((time) => this.seekTo(time));
    this.replayUI.onCameraChange((mode) => this.setCameraMode(mode));
    this.replayUI.onWatchHighlights(() => this.startHighlightReel());
    this.replayUI.onJumpToHighlight((index) => this.jumpToHighlight(index));
    this.replayUI.onExit(() => this.exit());

    this.replayUI.show();
    this.startLoop();
  }

  private togglePlayPause(): void {
    this.state.isPlaying = !this.state.isPlaying;
    if (this.state.isPlaying) {
      this.clock.start();
    }
    this.replayUI?.updateState(this.state);
  }

  private setSpeed(speed: ReplaySpeed): void {
    this.state.speed = speed;
    this.replayUI?.updateState(this.state);
  }

  private setCameraMode(mode: CameraMode): void {
    this.state.cameraMode = mode;
    this.replayUI?.updateState(this.state);
  }

  private seekTo(timeMs: number): void {
    if (!this.replayData) return;

    const targetTime = Math.max(0, Math.min(timeMs, this.replayData.duration));

    // Reset state and replay events from the beginning up to targetTime
    this.cleanupSpells();
    this.player1Health = MAX_HEALTH;
    this.player2Health = MAX_HEALTH;
    this.nextEventIndex = 0;

    // Fast-forward through events up to targetTime (without creating visuals for old events)
    for (let i = 0; i < this.replayData.events.length; i++) {
      const event = this.replayData.events[i];
      if (event.timestamp > targetTime) break;
      this.nextEventIndex = i + 1;

      // Only process damage events for health state reconstruction
      if (event.type === 'damage') {
        const dmg = event as ReplayDamageEvent;
        if (dmg.target === 'player1') {
          this.player1Health = dmg.newHealth;
        } else {
          this.player2Health = dmg.newHealth;
        }
      }
    }

    this.state.currentTime = targetTime;
    this.replayUI?.updateState(this.state);
    this.replayUI?.updateHealth(this.player1Health, this.player2Health);
  }

  private startHighlightReel(): void {
    if (this.state.highlights.length === 0) return;
    this.state.watchingHighlights = true;
    this.state.currentHighlightIndex = 0;
    this.jumpToHighlight(0);
  }

  private jumpToHighlight(index: number): void {
    if (index < 0 || index >= this.state.highlights.length) {
      this.state.watchingHighlights = false;
      this.state.currentHighlightIndex = -1;
      this.replayUI?.updateState(this.state);
      return;
    }

    this.state.currentHighlightIndex = index;
    const highlight = this.state.highlights[index];

    // Seek to 2 seconds before the highlight
    const seekTime = Math.max(0, highlight.timestamp - 2000);
    this.seekTo(seekTime);
    this.state.isPlaying = true;
    this.clock.start();

    // Auto-advance to next highlight after 5 seconds
    setTimeout(() => {
      if (this.state.watchingHighlights && this.state.currentHighlightIndex === index) {
        this.jumpToHighlight(index + 1);
      }
    }, 5000);

    this.replayUI?.updateState(this.state);
    this.replayUI?.showHighlightBanner(highlight);
  }

  private processEvent(event: ReplayEvent): void {
    switch (event.type) {
      case 'spell_cast':
        this.processSpellCast(event as ReplaySpellEvent);
        break;
      case 'damage':
        this.processDamage(event as ReplayDamageEvent);
        break;
      case 'game_over':
        this.state.isPlaying = false;
        this.replayUI?.updateState(this.state);
        break;
    }
  }

  private processSpellCast(event: ReplaySpellEvent): void {
    const casterPos = event.caster === 'player1' ? PLAYER1_POS : PLAYER2_POS;
    const targetPos = event.caster === 'player1' ? PLAYER2_POS : PLAYER1_POS;
    const startPos = casterPos.clone();
    const endPos = targetPos.clone();
    startPos.y = 1.5;
    endPos.y = 1.5;

    const isPlayer1 = event.caster === 'player1';
    const casterModel = isPlayer1 ? this.player1Model : this.player2Model;
    casterModel?.triggerCast(event.spell);

    // Play sound
    const soundMap: Record<string, string> = {
      reflect: 'shield_cast',
      teleport: 'shield_cast',
    };
    this.audioManager.play((soundMap[event.spell] ?? `${event.spell}_cast`) as any);

    switch (event.spell) {
      case 'fireball': {
        const fb = new Fireball(this.scene, startPos, endPos, 0, isPlayer1);
        this.activeSpells.add(fb);
        break;
      }
      case 'iceShard': {
        const ice = new IceShard(this.scene, startPos, endPos, 0, isPlayer1);
        this.activeSpells.add(ice);
        break;
      }
      case 'lightningBolt': {
        const bolt = new LightningBolt(this.scene, startPos, endPos, 0, isPlayer1);
        this.activeSpells.add(bolt);
        break;
      }
      case 'healSpell': {
        new HealSpell(this.scene, casterPos.clone());
        break;
      }
      case 'shield': {
        const shieldPos = casterPos.clone();
        if (isPlayer1) {
          if (this.player1Shield) this.player1Shield.dispose();
          this.player1Shield = new Shield(this.scene, shieldPos, 1, 2000);
        } else {
          if (this.player2Shield) this.player2Shield.dispose();
          this.player2Shield = new Shield(this.scene, shieldPos, 1, 2000);
        }
        break;
      }
    }
  }

  private processDamage(event: ReplayDamageEvent): void {
    if (event.target === 'player1') {
      this.player1Health = event.newHealth;
      if (event.amount > 0) {
        this.player1Model?.triggerDamage();
        this.audioManager.play('damage_taken');
      }
    } else {
      this.player2Health = event.newHealth;
      if (event.amount > 0) {
        this.player2Model?.triggerDamage();
      }
    }

    if (event.shieldAbsorbed > 0) {
      this.audioManager.play('shield_hit');
    }

    this.audioManager.play('fireball_impact');
    this.replayUI?.updateHealth(this.player1Health, this.player2Health);
  }

  private updateCamera(delta: number): void {
    switch (this.state.cameraMode) {
      case 'free': {
        if (!this.isDragging) {
          this.cameraAngle += this.cameraRotationSpeed * delta;
        }
        const x = Math.sin(this.cameraAngle) * this.cameraRadius;
        const z = Math.cos(this.cameraAngle) * this.cameraRadius;
        this.camera.position.set(x, this.cameraHeight, z);
        this.camera.lookAt(0, 1, 0);
        break;
      }
      case 'player1': {
        // Behind player1 looking at player2
        this.camera.position.set(PLAYER1_POS.x, 5, PLAYER1_POS.z + 5);
        this.camera.lookAt(PLAYER2_POS.x, 1, PLAYER2_POS.z);
        break;
      }
      case 'player2': {
        // Behind player2 looking at player1
        this.camera.position.set(PLAYER2_POS.x, 5, PLAYER2_POS.z - 5);
        this.camera.lookAt(PLAYER1_POS.x, 1, PLAYER1_POS.z);
        break;
      }
    }

    // Make name labels face camera
    if (this.player1Label) {
      this.player1Label.lookAt(this.camera.position);
    }
    if (this.player2Label) {
      this.player2Label.lookAt(this.camera.position);
    }
  }

  private cleanupSpells(): void {
    this.activeSpells.forEach((spell) => spell.dispose());
    this.activeSpells.clear();
    if (this.player1Shield) {
      this.player1Shield.dispose();
      this.player1Shield = null;
    }
    if (this.player2Shield) {
      this.player2Shield.dispose();
      this.player2Shield = null;
    }
  }

  private startLoop(): void {
    this.clock.start();
    const loop = () => {
      this.animationFrameId = requestAnimationFrame(loop);

      try {
        const rawDelta = this.clock.getDelta();
        const delta = this.state.isPlaying ? rawDelta * this.state.speed : 0;

        // Update arena
        if (this.arena) this.arena.update(rawDelta);

        // Update wizard animations
        this.player1Model?.update(rawDelta);
        this.player2Model?.update(rawDelta);

        // Advance replay time and process events
        if (this.state.isPlaying && this.replayData) {
          this.state.currentTime += delta * 1000;

          if (this.state.currentTime >= this.replayData.duration) {
            this.state.currentTime = this.replayData.duration;
            this.state.isPlaying = false;
            this.replayUI?.updateState(this.state);
          }

          // Process events up to current time
          while (
            this.nextEventIndex < this.replayData.events.length &&
            this.replayData.events[this.nextEventIndex].timestamp <= this.state.currentTime
          ) {
            this.processEvent(this.replayData.events[this.nextEventIndex]);
            this.nextEventIndex++;
          }
        }

        // Update spells
        const spellsToRemove: Spell[] = [];
        for (const spell of this.activeSpells) {
          const stillActive = spell.update(rawDelta);
          if (!stillActive) {
            if (spell.didHit()) {
              spell.triggerImpact();
            } else {
              spell.dispose();
            }
            spellsToRemove.push(spell);
          }
        }
        for (const spell of spellsToRemove) {
          this.activeSpells.delete(spell);
        }

        // Update shields
        if (this.player1Shield) {
          const active = this.player1Shield.update(rawDelta);
          if (!active) {
            this.player1Shield.dispose();
            this.player1Shield = null;
          }
        }
        if (this.player2Shield) {
          const active = this.player2Shield.update(rawDelta);
          if (!active) {
            this.player2Shield.dispose();
            this.player2Shield = null;
          }
        }

        // Update environmental effects
        if (this.environmentalEffects) this.environmentalEffects.update();

        // Update camera
        this.updateCamera(rawDelta);

        // Update timeline UI
        this.replayUI?.updateTimeline(this.state.currentTime);

        // Render
        if (this.postProcessing) {
          this.postProcessing.render();
        } else {
          this.renderer.render(this.scene, this.camera);
        }
      } catch (error) {
        console.error('Replay loop error:', error);
      }
    };

    loop();
  }

  private exit(): void {
    // Stop the loop
    cancelAnimationFrame(this.animationFrameId);
    window.removeEventListener('resize', this.handleResize);

    // Cleanup scene
    this.cleanupSpells();

    if (this.player1Group) this.scene.remove(this.player1Group);
    if (this.player2Group) this.scene.remove(this.player2Group);
    if (this.player1Label) this.scene.remove(this.player1Label);
    if (this.player2Label) this.scene.remove(this.player2Label);

    this.player1Model?.dispose();
    this.player2Model?.dispose();

    // Remove all scene children
    while (this.scene.children.length > 0) {
      const child = this.scene.children[0];
      this.scene.remove(child);
    }
    this.scene.fog = null;

    // Hide UI
    this.replayUI?.hide();
    this.replayUI = null;

    if (this.onExitCallback) {
      this.onExitCallback();
    }
  }

  private createTextSprite(text: string, color: string): THREE.Sprite {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d')!;
    canvas.width = 512;
    canvas.height = 128;

    context.font = 'Bold 48px Cinzel, serif';
    context.fillStyle = color;
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.shadowColor = 'rgba(0, 0, 0, 0.8)';
    context.shadowBlur = 8;
    context.shadowOffsetX = 2;
    context.shadowOffsetY = 2;
    context.fillText(text, 256, 64);

    const texture = new THREE.CanvasTexture(canvas);
    const material = new THREE.SpriteMaterial({ map: texture, transparent: true });
    const sprite = new THREE.Sprite(material);
    sprite.scale.set(4, 1, 1);
    return sprite;
  }
}
